import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { Course } from '../../shared/course.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { Pupil } from '../../shared/pupil.model';
import { SchoolType } from '../../shared/school-type.enum';
import { UpdatePupilDto } from '../../shared/update-pupils.dto';

@Component({
    selector: 'app-global-pupil-list',
    templateUrl: './global-pupil-list.component.html',
    styleUrl: './global-pupil-list.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GlobalPupilListComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { grade: "number?" };
    grade: number;

    year: number;
    isPast = false;

    pupils: Pupil[];
    courses: Course[];
    table: TableData<Pupil>;

    gradeLevels: number[];
    selectedGradeLevel = 7;
    coursesPerDomainAndPupil: OrdinaryObject<OrdinaryObject<number>> = {};

    gradeLevelCourses: Course[] = [];
    gradeLevelDomains: string[] = [];

    override async initParams(): Promise<boolean> {
        this.year = EnaioCertificatesTools.getCurrentYear();
        this.isPast = this.year < EnaioCertificatesTools.getCurrentYear();
        let settings = await this.service.restGetClassRegisterSettings(this.year, false);
        if (!this.service.isPupilListEditor(settings)) {
            await this.app.loadDataError("Sie besitzen nicht die notwendigen Berechtigungen, um diese Ansicht zu öffnen", false, this);
            return false;
        }

        this.courses = (await this.app.loadDataHandler(async () => RestEndpoint.main().query({year: this.year, isHalfYear: false, participants: true}).run("api/cer/classregister/courses").list(Course), this)).filter(c => c.isLeveledCourse);


        if (this.isPast) {
            this.pupils = [];
            for (let pupilId of Arrays.getUnique(Arrays.explode(this.courses, c => c.participants).map(p => p.pupilId))) {
                this.pupils = [...this.pupils, await this.service.restGetPupil(pupilId)];
            }
            this.pupils = Arrays.withoutNull(this.pupils);
        }
        else {
            this.pupils = (await this.service.restGetPupilsForYear(EnaioCertificatesTools.getSchoolYearFromYear(this.year))).filter(p => p.getSchoolType() != SchoolType.special);
        }

        for (let course of this.courses) {
            this.coursesPerDomainAndPupil[course.levelDomain] ??= {};
            for (let pupil of course.participants) {
                this.coursesPerDomainAndPupil[course.levelDomain][pupil.pupilId] = course.id;            
            }
        }

        this.gradeLevels = EnaioCertificatesTools.getAllGradeLevels().filter(gradeLevel => gradeLevel >= 7);

        this.gradeLevelChanged();

        this.app.updateNavigation("Schülerliste", [{ label: "Schülerliste", routerLink: ["/enaio-certificates", "global-pupil-list"] }]);

        return true;
    }

    gradeLevelChanged(): void {
        this.gradeLevelCourses = this.courses.filter(c => c.forGrades.includes(this.selectedGradeLevel));
        this.gradeLevelDomains = Arrays.sortByMultiple(Arrays.getUnique(this.gradeLevelCourses.map(c => c.levelDomain)), [c => EnaioCertificatesTools.isForeignLanguageDomain(c), c => c]);
        let coursesPerDomain = Arrays.toMultiMapKeys(this.gradeLevelCourses, c => c.levelDomain);
        let pupils = this.pupils.filter(p => p.grade == this.selectedGradeLevel);

        this.table = new TableData([
            new TableColumn("class", "Klasse", TableCellType.string, 3),
            new TableColumn("lastName", "Nachname", TableCellType.string, 15),
            new TableColumn("firstName", "Vorname", TableCellType.string, 15),
            new TableColumn("notePupilsList", "Bemerkung", TableCellType.string, 30, {
                editable: true
            }),
            new TableColumn("schoolCareer", "Laufbahn", TableCellType.string, 20, { 
                editable: true,
                editor: new FrontendField("field", "Feld", FrontendFieldType.dropdown, { 
                    listItems: FrontendFieldListItem.make(["Berufsreife", "Gymnasium", "Regionale Schule"])
                })
            }),
            new TableColumn("specialRequirement", "Sopäd. FSP", TableCellType.string, 20),
            new TableColumn("specialRequirement2", "Bedarfsform", TableCellType.string, 20),
            new TableColumn("secondForeignLanguage", "2. Fremdsprache", TableCellType.string, 20),
                /*       new TableColumn("level", "Anspruchsebene", TableCellType.string, 10, { editable: true, editor: new FrontendFieldDefinition("field", "Feld", FrontendFieldType.dropdown, {
                dropdownEditable: false,
                listItems: [new FrontendFieldListItem("", ""), ...EnaioCertificatesTools.getLevelTypesSingle().map(type => new FrontendFieldListItem(type, type))],
            })}),
       */ ...this.gradeLevelDomains.map(domain => new TableColumn("level_" + domain, domain, TableCellType.int, 10, {
           editable: true,
           valueFormatter: e => e.value == -1 ? "" : Arrays.findAndConvert(coursesPerDomain[domain], c => c.id == e.value, c => c.getLevelTitleShort(), "???"),
           //editableCallback: value => !EnaioCertificatesTools.isForeignLanguageDomain(domain) || domain == p.secondForeignLanguage,
           //getStyleClassCallback: (value, row) => value != -1 && this.table.rows[row].getValue("level") != "" && this.courses.find(c => c.id == value)?.levelTypeMatches(this.table.rows[row].getValue("level")) ? "xbg-red-200" : null,
           editor: new FrontendField("field", "Feld", FrontendFieldType.dropdown, {
               listItems: [new FrontendFieldListItem(-1, ""), ...coursesPerDomain[domain].map(c => new FrontendFieldListItem(c.id, c.getLevelTitleShort()))]
           })
       }))
        ], pupils.map(p => new TableRow(Objects.toString(p.id), p, {
            class: p.getClassDisplayName(),
            lastName: p.lastName,
            firstName: p.firstName,
            notePupilsList: p.notePupilsList,
            schoolCareer: p.schoolCareer,
            specialRequirement: p.specialRequirement,
            specialRequirement2: p.specialRequirement2,
            secondForeignLanguage: p.secondForeignLanguage,
        //    level: p.level,
            ...Arrays.toOrdinaryObject(this.gradeLevelDomains, domain => "level_" + domain, domain => new TableCell(this.coursesPerDomainAndPupil[domain][p.id] ?? -1, { 
                styleClass: EnaioCertificatesTools.isForeignLanguageDomain(domain) && domain != p.secondForeignLanguage ? "xbg-gray-400" : null}))
        }), ["lastName"]));
    }


    getChanges(): { changes: string[]; dto: UpdatePupilDto[] } {
        let changes: string[] = [];
        let dto: UpdatePupilDto[] = [];
        for (let row of this.table.rows) {
            let item = new UpdatePupilDto(row.raw.id);
            if (row.raw.notePupilsList != row.values.notePupilsList) {
                item.notePupilsList = row.values.notePupilsList;
                changes.push(row.raw.getFullName() + ": Bemerkung geändert"); 
            }
            if (row.raw.schoolCareer != row.values.schoolCareer) {
                item.schoolCareer = row.values.schoolCareer;
                changes.push(row.raw.getFullName() + ": Laufbahn geändert"); 
            }
        /*    if (row.raw.level != row.values.level) {
                item.level = row.values.level;
                changes.push(row.raw.getFullName() + ": Anspruchsebene geändert"); 
            }
          */  for (let domain of this.gradeLevelDomains) {
                let oldValue = this.coursesPerDomainAndPupil[domain][row.raw.id] ?? -1;
                let newValue = row.getValue("level_" + domain) as number;
                if (oldValue != newValue) {
                    if (oldValue != -1) {
                        item.removeFromCourses.push(oldValue);
                    }
                    if (newValue != -1) {
                        item.addToCourses.push(newValue);
                    }
                    changes.push(row.raw.getFullName() + ": Kurs für " + domain + " geändert: " + 
                        (this.courses.find(c => c.id == oldValue)?.getLevelTitleShort() ?? "<Nicht zugeordnet>") + " -> " + (this.courses.find(c => c.id == newValue)?.getLevelTitleShort() ?? "<Nicht zugeordnet>"));
                }
            }
            if (item.notePupilsList != null || item.schoolCareer != null || item.level != null || item.removeFromCourses.length > 0 || item.addToCourses.length > 0) {
                dto.push(item);
            }
        }
        return { changes, dto };
    }

    isChanged(): boolean {
        return this.getChanges().dto.length > 0;
    }

    async save(): Promise<void> {
        let {changes, dto} = this.getChanges();
        if (dto.length > 0 &&
            await this.app.dialog.yesNo("Die folgenden Informationen werden aktualisiert. Fortfahren?<br><br>" + Arrays.itemsToString(changes, "<br>"), "Bestätigung")) {
            await this.app.saveDataHandler(async () => RestEndpoint.main().query({year: this.year}).post().body(dto).run("api/cer/pupil/updatePupils").getText(), this);
        }
        await this.initParams();
    }
}
